<script>
import { getExperimentationService } from '../services/experimentation/experimentationService';
import OptimizelyStrategy from '../services/experimentation/optimizelyStrategy';
import routeNames from '@/router/routeNames';
import { helpers as notificationHelpers } from '@/store/modules/notifications';
import { helpers as referralHelpers } from '@/store/modules/referral';
import {
  helpers as researcherHelpers,
  namespace as researcherNamespace,
} from '@/store/modules/researcherSignup';
import { SHOW_SNACKBAR } from '@/store/types';
import Api from '@/utils/api';
import {
  exists,
  isEmail,
  invalidNameMessage,
  minLength,
} from '@/utils/validationRules';
import Storage from 'Shared/helpers/storageManager';
import _ from 'lodash';

export default {
  data() {
    // check for an email from the invitation.
    const email = _.get(
      this.$store.state[researcherNamespace],
      'invitation.invitation.email',
      ''
    );
    return {
      confirmPassword: '',
      email,
      firstName: '',
      formIsValid: false,
      isSubmitting: false,
      lastName: '',
      password: '',
      revealPassword: false,
      rules: {
        exists: () => true,
        isEmail: () => true,
        invalidNameMessage: () => true,
        minLength: () => true,
        passwordsMatch: () => true,
      },
    };
  },
  computed: {
    ...researcherHelpers.mapState({ invitation: 'invitation' }),
    ...referralHelpers.mapState({ referralCode: 'referralCode' }),
    hasInvitationEmail() {
      const invitationEmail = _.get(this.invitation, 'invitation.email');
      return !_.isEmpty(invitationEmail);
    },
    passwordInputType() {
      return this.revealPassword ? 'text' : 'password';
    },
    passwordIcon() {
      return this.revealPassword ? 'fas fa-eye' : 'fas fa-eye-slash';
    },
    submitButtonText() {
      if (this.hasInvitationEmail) return 'Accept My Invitation';
      return 'Create an Account';
    },
  },
  methods: {
    ...notificationHelpers.mapActions({ showSnackbar: SHOW_SNACKBAR }),
    handleSignupError(message, redirect) {
      this.isSubmitting = false;
      this.showSnackbar({ message });
      if (redirect) {
        setTimeout(() => window.location.assign(redirect), 3000);
      }
    },
    togglePasswordReveal() {
      this.revealPassword = !this.revealPassword;
    },
    setRules() {
      this.rules = {
        exists,
        isEmail,
        invalidNameMessage,
        minLength,
        passwordsMatch: (password) => (confirm) =>
          password === confirm || 'Passwords must match',
      };
    },
    async signupResearcher() {
      this.setRules();
      this.$nextTick(async () => {
        await this.$refs.form.validate();
        if (!this.formIsValid) return;
        this.isSubmitting = true;

        const genericMsg =
          'There was a problem signing you up. Please try again later.';
        try {
          const userParams = {
            email: this.email,
            firstName: this.firstName,
            lastName: this.lastName,
          };
          if (this.invitation) userParams.invitation = this.invitation;
          if (this.referralCode) userParams.referralCode = this.referralCode;

          const res = await Api.post('/signup', {
            ...userParams,
            password: this.password,
          });
          const { errorDetails, redirect, success, userId } = res.data;
          if (!success) {
            const message = errorDetails || genericMsg;
            return this.handleSignupError(message, redirect);
          }
          if (redirect) {
            return window.location.assign(redirect);
          }
          return this.$router.push({ name: routeNames.organization });
        } catch (e) {
          return this.handleSignupError(genericMsg);
        }
      });
    },
  },
};
</script>

<template>
  <v-form
    v-model="formIsValid"
    @submit.prevent="signupResearcher"
    method="POST"
    ref="form"
  >
    <v-row>
      <v-col>
        <base-input
          v-model="firstName"
          :rules="[rules.exists, rules.invalidNameMessage]"
          :value="firstName"
          label="First Name"
          name="firstName"
          placeholder="Jane"
          type="text"
        />
      </v-col>
      <v-col>
        <base-input
          v-model="lastName"
          :rules="[rules.exists, rules.invalidNameMessage]"
          :value="lastName"
          label="Last Name"
          name="lastName"
          placeholder="Doe"
          type="text"
        />
      </v-col>
    </v-row>

    <base-input
      v-model="email"
      :disabled="hasInvitationEmail"
      :value="email"
      :rules="[rules.exists, rules.isEmail]"
      label="Work Email"
      name="email"
      placeholder="name@emaildomain.com"
      type="text"
    />
    <v-row>
      <v-col>
        <base-input
          v-model="password"
          :append-icon="passwordIcon"
          :rules="[rules.exists, rules.minLength(8, 'Passwords')]"
          :type="passwordInputType"
          :value="password"
          @click:append="togglePasswordReveal"
          label="Password"
          name="password"
          placeholder="8+ character minimum"
        />
      </v-col>
      <v-col>
        <base-input
          v-model="confirmPassword"
          :append-icon="passwordIcon"
          :rules="[rules.exists, rules.passwordsMatch(password)]"
          :type="passwordInputType"
          :value="confirmPassword"
          @click:append="togglePasswordReveal"
          label="Confirm Password"
          name="confirmPassword"
          placeholder="Passwords must match"
        />
      </v-col>
    </v-row>
    <v-btn :loading="isSubmitting" block color="primary" type="submit">
      {{ submitButtonText }}
    </v-btn>
  </v-form>
</template>

<style scoped lang="scss">
.v-input--checkbox::v-deep {
  .v-label {
    color: $dark-text-color;
    font-weight: bold;
  }
}
</style>
